import "./ChoiceField.scss";

import clsx from "clsx";
import { get, isEqual } from "lodash-es";
import type { ChangeEvent, FC, MouseEvent } from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { Doc } from "sharedb/lib/client";

import { PresenceDataType } from "../../../shared/types/sharedb";
import {
  ChoiceFieldType,
  MultipleChoiceCellType,
  PathType,
  WorksheetType,
} from "../../../shared/types/worksheet";

import { getPresenceViewTypeFromPageTabVariant } from "components/materials/page/helpers";
import { isProjectorView } from "components/materials/presentation/projector/projector-messaging";
import { DocViewContext } from "providers/DocViewProvider";
import { useAppSelector } from "store/index";
import { selectCurrentUserId, selectSelectedPageTabVariant } from "store/selectors";
import { BUTTON_EVENT_CATEGORY, getPageFieldProps, trackEvent } from "utils/amplitude";
import { useFieldHtmlId } from "utils/hooks/use-field-html-id";
import { rollbarAndLogError } from "utils/logger";

interface ChoiceFieldProps extends ChoiceFieldType {
  path: PathType;
  fieldPath: PathType;
  doc: Doc<WorksheetType>;
  instructionsFieldId?: string;
}

type MCPresenceDataType = {
  index: number;
  user_id: string;
  srcPresenceId: string;
};

export const ChoiceField: FC<ChoiceFieldProps> = ({
  c: choices,
  path,
  fieldPath,
  doc,
  m: multiple,
  instructionsFieldId,
}) => {
  const userId = useAppSelector(selectCurrentUserId);
  const {
    readOnly: docReadOnly,
    isAuthoringPage,
    canInteractAsStudent,
    pageId,
  } = useContext(DocViewContext);
  const [userPresences, setUserPresences] = useState<MCPresenceDataType[]>([]);
  const readOnly = docReadOnly || !canInteractAsStudent;
  const tabVariant = useAppSelector(selectSelectedPageTabVariant);
  const presenceViewType = getPresenceViewTypeFromPageTabVariant(tabVariant);

  const data = get(doc.data, path) as MultipleChoiceCellType;
  const allowsMultipleResponses = Boolean(multiple);
  const allowUnSelect = isAuthoringPage && !allowsMultipleResponses;
  const selectedIndexRef = useRef<number | null>(data?.[0]);

  const htmlName = useFieldHtmlId(doc, fieldPath);

  // This is a bit of a hack but we want Authors to be able to unselect a radio button
  const onAuthorClick = (event: MouseEvent<HTMLInputElement>) => {
    const index = parseInt(event.currentTarget.value, 10);
    const selected = selectedIndexRef.current !== index;
    selectedIndexRef.current = selected ? index : null;

    const p = [...path, 0];
    const op = [{ p, ld: null, li: selected ? index : null }];
    doc.submitOp(op);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      currentTarget: { checked, value },
    } = event;
    const index = parseInt(value, 10);

    trackEvent("Multiple Choice - Selected", {
      ...BUTTON_EVENT_CATEGORY,
      ...getPageFieldProps(pageId, path),
      fieldValue: index,
    });

    const p = [...path, allowsMultipleResponses ? index : 0];
    const op = [{ p, ld: null, li: checked ? index : null }];

    doc.submitOp(op);

    if (!readOnly && userId) {
      doc.localPresence.submit({
        t: "mc",
        v: presenceViewType,
        i: index,
        p: path,
        u: userId,
      });
    }
  };

  useEffect(() => {
    const onPresence = (srcPresenceId: string, presence: PresenceDataType) => {
      // We don't show presence on the projector view, because the scaling
      // interferes with the cursor placement, making it look buggy.
      if (isProjectorView()) {
        return;
      }
      if (!srcPresenceId || !presence || !presence.p) {
        // Remove the cursor if the source is disconnected
        setUserPresences((prev) => prev.filter((p) => p.srcPresenceId !== srcPresenceId));
        return;
      }

      if (!isEqual(presence.p, path)) {
        // Remove presence if the source user is active in another field
        setUserPresences((prev) => prev.filter((p) => p.user_id !== presence.u));
        return;
      }

      if (presence.t !== "mc") {
        rollbarAndLogError("Got invalid presence for ChoiceField", {
          presence,
        });
        return;
      }

      // We only display presence coming from the same view as the current tab,
      // with the following exceptions:
      // - On the class responses tab, you can see presence from everyone, generally, as students only have
      //   permission to view the class responses tab if they are allowed to see everyone's responses.
      // - Instructors editing the template can for BLOCKS_PATH (editing instructions, etc.) will show presence
      //   on the student draft too.
      const isClassResponses = presenceViewType === "c";
      const matchingPresenceView = presence.v === presenceViewType;
      const comesFromTemplate = presence.v === "t";
      const isDraftPage = presenceViewType === "d";
      if (!matchingPresenceView && !isClassResponses && !(comesFromTemplate && isDraftPage)) {
        return;
      }

      setUserPresences((prev) => [
        ...prev.filter((p) => p.user_id !== presence.u),
        { index: presence.i, user_id: presence.u, srcPresenceId },
      ]);
    };

    doc.presence.on("receive", onPresence);
    return () => {
      doc.presence.off("receive", onPresence);
    };
  }, [doc, path, presenceViewType]);

  return (
    <fieldset aria-labelledby={instructionsFieldId} className="px-2">
      {choices.map((choice, index) => {
        const type = allowsMultipleResponses ? "checkbox" : "radio";
        const label = choice.l;

        // TODO: This is fully working in terms of highlighting the user's presence,
        // but displaying the name of the user as a cursor is currently only implemented
        // in CodeEditor/Cursor.tsx, which is specific to the code editor. We should generalize this.
        const highlighted = userPresences.some((p) => p.index === index);

        // Multiple responses are stored in `data` in the format [0, null, 2] to denote index 0 and 2
        // are selected. Note in this example there may be more than 3 options to select, but all the
        // options after index 2 are undefined and thus not selected.
        const isChecked = !data
          ? false
          : allowsMultipleResponses
            ? data[index] === index
            : index === data[0];

        return (
          <label
            className={clsx(
              "block-choice-button body-s flex items-center",
              highlighted && "highlighted",
            )}
            key={choice.id}
          >
            <input
              checked={isChecked}
              className="mr-2 flex-none"
              disabled={readOnly}
              name={htmlName}
              readOnly={readOnly}
              type={type}
              value={index}
              onChange={!readOnly && !allowUnSelect ? onChange : undefined}
              onClick={allowUnSelect ? onAuthorClick : undefined}
            />
            <div className="block-choice-button-text leading-6">{label}</div>
          </label>
        );
      })}
    </fieldset>
  );
};
